import React, { useEffect, useRef, useState } from 'react';
// Styles provided by RestaurantOnboarding.css

const CERT_TYPES  = ['application/pdf'];
const CERT_MAX_MB = 10;

const fmtSize = (bytes) => {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
};

const StepRestaurantBilling = ({ formData, onUpdate, errors = {}, onClearError }) => {
  const certInputRef = useRef(null);
  const [certDrag, setCertDrag]   = useState(false);
  const [certError, setCertError] = useState(null);

  /* Build the address string from the location step */
  const localAddress = [
    formData.address_main,
    formData.address_cross,
    formData.address_ref,
  ].filter(Boolean).join(', ');

  /* Auto-fill billing_address on first render if it's still empty */
  useEffect(() => {
    if (!formData.billing_address && localAddress) {
      onUpdate({ billing_address: localAddress });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const isAutoFilled = !!localAddress && formData.billing_address === localAddress;

  const handleChange = (field, value) => {
    onUpdate({ [field]: value });
    if (onClearError) onClearError(field);
  };

  /* RUC: digits only, max 13 */
  const handleRuc = (raw) => {
    const digits = raw.replace(/\D/g, '').slice(0, 13);
    handleChange('ruc', digits);
  };

  /* Certificate upload */
  const processCert = (file) => {
    if (!file) return;
    if (!CERT_TYPES.includes(file.type)) {
      setCertError('Solo se aceptan archivos PDF');
      return;
    }
    if (file.size > CERT_MAX_MB * 1024 * 1024) {
      setCertError(`El archivo no puede superar ${CERT_MAX_MB} MB`);
      return;
    }
    setCertError(null);
    onUpdate({ ruc_certificate: file });
    if (onClearError) onClearError('ruc_certificate');
  };

  const handleCertDrop = (e) => {
    e.preventDefault();
    setCertDrag(false);
    const file = e.dataTransfer.files?.[0];
    if (file) processCert(file);
  };

  const handleCertRemove = () => {
    onUpdate({ ruc_certificate: null });
    setCertError(null);
    if (certInputRef.current) certInputRef.current.value = '';
  };

  /* DNI: digits only, max 10 */
  const handleDni = (raw) => {
    const digits = raw.replace(/\D/g, '').slice(0, 10);
    handleChange('representative_dni', digits);
    /* Sincronizar con el número de documento bancario si el tipo es CI */
    if ((formData.bank_ci_type || 'CI') === 'CI') {
      onUpdate({ representative_dni: digits, bank_ci_number: digits });
    }
  };

  return (
    <div className="ro-body ro-basic">

      <div className="ro-step-hd">
        <span className="ro-eyebrow ro-eyebrow--accent">Paso 8 · Facturación</span>
        <h2 className="ro-title">Datos de facturación</h2>
        <p className="ro-subtitle">
          Esta información es obligatoria para emitir comprobantes electrónicos en el SRI.
        </p>
      </div>

      {/* ── Razón Social ── */}
      <div className="ro-field">
        <label className="ro-lbl">
          Razón Social<span className="ro-req">*</span>
        </label>
        <div className={`ro-inputwrap${errors.business_name ? ' ro-inputwrap--err' : ''}`}>
          <span className="ro-inputico" aria-hidden>🏢</span>
          <input
            type="text"
            className="ro-input--clean"
            placeholder="Ej. El Horno de la Abuela"
            value={formData.business_name || ''}
            onChange={(e) => handleChange('business_name', e.target.value)}
            maxLength={200}
          />
        </div>
        {errors.business_name && <span className="ro-err-msg">{errors.business_name}</span>}
      </div>

      {/* ── RUC ── */}
      <div className="ro-field">
        <label className="ro-lbl">
          RUC<span className="ro-req">*</span>
        </label>
        <div className={`ro-inputwrap${errors.ruc ? ' ro-inputwrap--err' : ''}`}>
          <span className="ro-inputico" aria-hidden>🔢</span>
          <input
            type="text"
            inputMode="numeric"
            className="ro-input--clean"
            placeholder="13 dígitos"
            value={formData.ruc || ''}
            onChange={(e) => handleRuc(e.target.value)}
            maxLength={13}
          />
          <span className="ro-inputsuffix ro-phone-count">
            {(formData.ruc || '').length}/13
          </span>
        </div>
        {errors.ruc && <span className="ro-err-msg">{errors.ruc}</span>}
      </div>

      {/* ── Certificado RUC ── */}
      <div className="ro-field">
        <label className="ro-lbl">
          Certificado de RUC activo<span className="ro-req">*</span>
        </label>
        <p className="ro-field-hint">
          Descárgalo desde el portal del SRI y súbelo aquí. Solo se aceptan archivos PDF.
        </p>

        {formData.ruc_certificate ? (
          /* ── Archivo seleccionado ── */
          <div className="ro-cert-file">
            <span className="ro-cert-file-ico" aria-hidden>
              {formData.ruc_certificate.type === 'application/pdf' ? '📄' : '🖼️'}
            </span>
            <div className="ro-cert-file-meta">
              <p className="ro-cert-file-name">{formData.ruc_certificate.name}</p>
              <p className="ro-cert-file-sub">
                {formData.ruc_certificate.type === 'application/pdf' ? 'PDF' :
                  formData.ruc_certificate.type === 'image/jpeg' ? 'JPG' : 'PNG'}
                {' · '}{fmtSize(formData.ruc_certificate.size)}
              </p>
            </div>
            <button
              type="button"
              className="ro-cert-file-change"
              onClick={() => certInputRef.current?.click()}
            >
              Cambiar
            </button>
            <button
              type="button"
              className="ro-cert-file-remove"
              onClick={handleCertRemove}
              aria-label="Eliminar archivo"
            >
              ✕
            </button>
          </div>
        ) : (
          /* ── Zona de drop ── */
          <div
            className={`ro-photo-zone ro-cert-zone${certDrag ? ' ro-photo-zone--over' : ''}${(certError || errors.ruc_certificate) ? ' ro-photo-zone--err' : ''}`}
            onClick={() => certInputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setCertDrag(true); }}
            onDragLeave={() => setCertDrag(false)}
            onDrop={handleCertDrop}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && certInputRef.current?.click()}
            aria-label="Subir certificado RUC"
          >
            <div className="ro-photo-zone-ico" aria-hidden>
              <svg width="38" height="38" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
                <line x1="12" y1="18" x2="12" y2="12"/>
                <line x1="9" y1="15" x2="15" y2="15"/>
              </svg>
            </div>
            <p className="ro-photo-zone-txt">
              <strong>Arrastra tu certificado aquí</strong>
              <br />o haz clic para seleccionar
            </p>
            <p className="ro-photo-zone-hint">Solo PDF · Máx. {CERT_MAX_MB} MB</p>
          </div>
        )}

        <input
          ref={certInputRef}
          type="file"
          accept=".pdf"
          style={{ display: 'none' }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) processCert(file);
          }}
        />

        {(certError || errors.ruc_certificate) && (
          <span className="ro-err-msg ro-err-msg--block">
            {certError || errors.ruc_certificate}
          </span>
        )}
      </div>

      {/* ── Divider ── */}
      <div className="ro-billing-divider">
        <span>Representante legal</span>
      </div>

      {/* ── Nombre + Apellido (2 col) ── */}
      <div className="ro-billing-row">
        <div className="ro-field">
          <label className="ro-lbl">
            Nombre<span className="ro-req">*</span>
          </label>
          <div className={`ro-inputwrap${errors.representative_name ? ' ro-inputwrap--err' : ''}`}>
            <span className="ro-inputico" aria-hidden>👤</span>
            <input
              type="text"
              className="ro-input--clean"
              placeholder="Ej. Carlos"
              value={formData.representative_name || ''}
              onChange={(e) => handleChange('representative_name', e.target.value)}
              maxLength={100}
            />
          </div>
          {errors.representative_name && (
            <span className="ro-err-msg">{errors.representative_name}</span>
          )}
        </div>

        <div className="ro-field">
          <label className="ro-lbl">
            Apellido<span className="ro-req">*</span>
          </label>
          <div className={`ro-inputwrap${errors.representative_last_name ? ' ro-inputwrap--err' : ''}`}>
            <span className="ro-inputico" aria-hidden>👤</span>
            <input
              type="text"
              className="ro-input--clean"
              placeholder="Ej. Pérez"
              value={formData.representative_last_name || ''}
              onChange={(e) => handleChange('representative_last_name', e.target.value)}
              maxLength={100}
            />
          </div>
          {errors.representative_last_name && (
            <span className="ro-err-msg">{errors.representative_last_name}</span>
          )}
        </div>
      </div>

      {/* ── Cédula ── */}
      <div className="ro-field">
        <label className="ro-lbl">
          Cédula del representante<span className="ro-req">*</span>
        </label>
        <div className={`ro-inputwrap${errors.representative_dni ? ' ro-inputwrap--err' : ''}`}>
          <span className="ro-inputico" aria-hidden>🪪</span>
          <input
            type="text"
            inputMode="numeric"
            className="ro-input--clean"
            placeholder="10 dígitos"
            value={formData.representative_dni || ''}
            onChange={(e) => handleDni(e.target.value)}
            maxLength={10}
          />
          <span className="ro-inputsuffix ro-phone-count">
            {(formData.representative_dni || '').length}/10
          </span>
        </div>
        {errors.representative_dni && (
          <span className="ro-err-msg">{errors.representative_dni}</span>
        )}
      </div>

      {/* ── Dirección de facturación (al final, auto-completada) ── */}
      <div className="ro-field">
        <div className="ro-billing-addr-hd">
          <label className="ro-lbl">
            Dirección de facturación<span className="ro-req">*</span>
          </label>
          {isAutoFilled && (
            <span className="ro-billing-autofill-badge">
              📍 Completada desde tu local
            </span>
          )}
          {localAddress && !isAutoFilled && (
            <button
              type="button"
              className="ro-billing-restore-btn"
              onClick={() => handleChange('billing_address', localAddress)}
            >
              ↺ Restaurar
            </button>
          )}
        </div>

        <div className={`ro-inputwrap${errors.billing_address ? ' ro-inputwrap--err' : ''}`}>
          <span className="ro-inputico" aria-hidden>🏠</span>
          <input
            type="text"
            className="ro-input--clean"
            placeholder="Ej. Av. 6 de Diciembre y Colón"
            value={formData.billing_address || ''}
            onChange={(e) => handleChange('billing_address', e.target.value)}
            maxLength={300}
          />
        </div>
        {errors.billing_address && <span className="ro-err-msg">{errors.billing_address}</span>}
      </div>

    </div>
  );
};

export default StepRestaurantBilling;
